import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Image as ImageIcon, Crop, Trash2 } from 'lucide-react';
import ImageCropDialog from '@/components/shared/ImageCropDialog';

interface SimpleImageUploadProps {
  image: string;
  side: 'front' | 'back' | 'other';
  onImageUploaded: (file: File) => void;
  onImageDeleted?: () => Promise<void>;
}

const SimpleImageUpload: React.FC<SimpleImageUploadProps> = ({
  image,
  side,
  onImageUploaded,
  onImageDeleted
}) => {
  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    onImageUploaded(file);
  };

  const handleChange = (e: React.MouseEvent) => {
    e.preventDefault(); // Prevent form submission
    fileInputRef.current?.click();
  };

  const handleCropClick = (e: React.MouseEvent) => {
    e.preventDefault(); // Prevent form submission
    if (image) {
      setCropDialogOpen(true);
    }
  };

  const handleCroppedImage = async (croppedImageUrl: string) => {
    try {
      // Convert data URL to Blob
      const response = await fetch(croppedImageUrl);
      const blob = await response.blob();

      // Create a file from the blob
      const file = new File([blob], `cropped_${side}.jpg`, { type: 'image/jpeg' });
      onImageUploaded(file);
    } catch (error) {
      console.error('Error saving cropped image:', error);
    }
  };

  return (
    <div className="relative">
      <div className="aspect-[3/2] bg-muted rounded-lg overflow-hidden">
        {image ? (
          <img
            src={image}
            alt={`${side} of banknote`}
            className="w-full h-full object-contain"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <ImageIcon className="h-12 w-12 text-muted-foreground" />
          </div>
        )}
      </div>

      <div className="absolute bottom-2 right-2 flex gap-2">
        {image && (
          <>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="bg-white/80 hover:bg-white"
              onClick={handleCropClick}
            >
              <Crop className="h-4 w-4" />
            </Button>
            {onImageDeleted && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="bg-white/80 hover:bg-white text-destructive hover:text-destructive"
                onClick={async () => {
                  if (onImageDeleted) {
                    await onImageDeleted();
                  }
                }}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </>
        )}
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="bg-white/80 hover:bg-white"
          onClick={handleChange}
        >
          {image ? 'Change' : 'Upload'}
        </Button>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
      />

      {cropDialogOpen && image && (
        <ImageCropDialog
          imageUrl={image}
          open={cropDialogOpen}
          onClose={() => setCropDialogOpen(false)}
          onSave={handleCroppedImage}
          title={`Edit ${side === 'front' ? 'Front' : 'Back'} Image`}
        />
      )}
    </div>
  );
};

export default SimpleImageUpload;
